"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCreateRepeater = void 0;
/* eslint-disable @typescript-eslint/ban-types */
var react_1 = __importDefault(require("react"));
var react_2 = require("react");
var form_manager_1 = require("../../form-manager");
/**
 * A custom hook that holds most of the internal logic for the <Repeater /> Component.
 * @date 10/30/2022 - 12:53:13 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {string} repetitionSingularTitle
 * @param {UseCreateRepeaterParams<Repetition>} {
    initialData,
    renderer,
    formManager,
    name,
  }
 * @returns {{ repetitions: any; add: () => void; remove: (index?: number) => void; getHeading: (index: number) => string; render: (rep: Repetition, formManager?: any) => any; getRepeti...}
 */
var useCreateRepeater = function (repetitionSingularTitle, _a) {
    var initialData = _a.initialData, renderer = _a.renderer, EXTERNAL_FORM_MANAGER = _a.formManager, name = _a.name, form = _a.form;
    /** @var counter - a function that returns the next increment for an internal counter, used to generate scoped unique indexes for repeated form inputs*/
    var counter = useCounter();
    var INTERNAL_FORM_MANAGER = useInternalFormManager({
        EXTERNAL_FORM_MANAGER: EXTERNAL_FORM_MANAGER,
        name: name,
        initialData: initialData,
    });
    var _b = (0, react_2.useState)((EXTERNAL_FORM_MANAGER === null || EXTERNAL_FORM_MANAGER === void 0 ? void 0 : EXTERNAL_FORM_MANAGER.getRepeatedFormDataByName(name)) || []), repetitions = _b[0], setRepetitions = _b[1];
    var _c = (0, react_2.useState)((EXTERNAL_FORM_MANAGER === null || EXTERNAL_FORM_MANAGER === void 0 ? void 0 : EXTERNAL_FORM_MANAGER.repeatedFormData[name]) || []), repetitionFormData = _c[0], _setRepetitionFormData = _c[1];
    var setRepetitionFormData = function (val) {
        _setRepetitionFormData(val);
        if (INTERNAL_FORM_MANAGER && EXTERNAL_FORM_MANAGER) {
            EXTERNAL_FORM_MANAGER.repeatedFormData[name] = val;
        }
    };
    var cloneFormManager = createCloneFormManager(counter);
    var factoryArgs = {
        setRepetitions: setRepetitions,
        repetitions: repetitions,
        initialData: initialData,
        renderer: renderer,
        cloneFormManager: cloneFormManager,
        repetitionFormData: repetitionFormData,
        setRepetitionFormData: setRepetitionFormData,
        INTERNAL_FORM_MANAGER: INTERNAL_FORM_MANAGER,
        form: form,
    };
    var add = createAdd(factoryArgs);
    var addWithValues = createAddWithValues(factoryArgs);
    var remove = createRemove(factoryArgs);
    var render = createRender(factoryArgs);
    var getHeading = createGetHeading(repetitionSingularTitle);
    var move = createMove(factoryArgs);
    return {
        repetitions: repetitions,
        add: add,
        addWithValues: addWithValues,
        remove: remove,
        getHeading: getHeading,
        render: render,
        getRepetitionFormData: function (i) { return repetitionFormData[i]; },
        move: move,
        internalFormManager: INTERNAL_FORM_MANAGER,
    };
};
exports.useCreateRepeater = useCreateRepeater;
function createAddWithValues(_a) {
    var setRepetitions = _a.setRepetitions, repetitions = _a.repetitions, repetitionFormData = _a.repetitionFormData, setRepetitionFormData = _a.setRepetitionFormData, cloneFormManager = _a.cloneFormManager;
    return function (newValues) {
        setRepetitions(__spreadArray(__spreadArray([], repetitions, true), [newValues], false));
        setRepetitionFormData(__spreadArray(__spreadArray([], repetitionFormData, true), [
            cloneFormManager(undefined, newValues),
        ], false));
    };
}
/**
 * A hook that takes in a formManager, and  uses it to create a seperate
 * formManager for the repeated data (using that as its initialData)
 * that will be contained within the original formManager's
 * repeatedFormData property.
 *
 * @date 10/30/2022 - 1:06:11 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {({
 *   EXTERNAL_FORM_MANAGER: FormManager<Repetition> | undefined;
 *   name: keyof Repetition;
 *   initialData: Repetition;
 * })} {
 *   EXTERNAL_FORM_MANAGER,
 *   name,
 *   initialData,
 * }
 * @returns {*}
 */
function useInternalFormManager(_a) {
    var _b;
    var EXTERNAL_FORM_MANAGER = _a.EXTERNAL_FORM_MANAGER, name = _a.name, initialData = _a.initialData;
    var fm = (0, react_2.useState)(new form_manager_1.FormManager((EXTERNAL_FORM_MANAGER === null || EXTERNAL_FORM_MANAGER === void 0 ? void 0 : EXTERNAL_FORM_MANAGER.getInitialRepeaterData(name)) || initialData, (_b = EXTERNAL_FORM_MANAGER === null || EXTERNAL_FORM_MANAGER === void 0 ? void 0 : EXTERNAL_FORM_MANAGER.getValidationSchema()) === null || _b === void 0 ? void 0 : _b.fields[name]))[0];
    return fm;
}
/**
 * A factory function used to create a `cloneFormManager` function, which
 * clones a FormManager instance, and returns a new instance with a unique
 * index appended to every field name in order to maintain a unique render.
 * These modified values are normalized/removed when retrieving the data from the
 * FormManager instance.
 *
 * @date 10/30/2022 - 1:01:20 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {() => number} counter
 * @returns {number) => (INTERNAL_FORM_MANAGER: any, initialData: Repetition) => any}
 */
function createCloneFormManager(counter) {
    return function (INTERNAL_FORM_MANAGER, initialData) {
        var formData = (INTERNAL_FORM_MANAGER === null || INTERNAL_FORM_MANAGER === void 0 ? void 0 : INTERNAL_FORM_MANAGER._initialData) || initialData;
        var i = counter();
        var formDataWithIndexedKeys = Object.entries(formData).map(function (_a) {
            var k = _a[0], v = _a[1];
            return [
                k + "-" + i,
                v,
            ];
        });
        return new form_manager_1.FormManager(Object.fromEntries(formDataWithIndexedKeys), INTERNAL_FORM_MANAGER === null || INTERNAL_FORM_MANAGER === void 0 ? void 0 : INTERNAL_FORM_MANAGER.getValidationSchema());
    };
}
/**
 * A utility function used to generate the heading string for a single repetition.
 * @date 10/30/2022 - 1:00:50 PM
 *
 * @param {string} repetitionSingularTitle
 * @returns {(index: number) => string}
 */
function createGetHeading(repetitionSingularTitle) {
    return function (index) { return repetitionSingularTitle + " #" + (index + 1); };
}
/**
 * A factory function used to create an `add` function, which
 * adds a new repetition to the <Repeater /> Component.
 * @date 10/30/2022 - 1:00:07 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {CreateFactoryArgs<Repetition>} {
 * setRepetitions,
 * repetitions,
 * initialData,
 * repetitionFormData,
 * setRepetitionFormData,
 * cloneFormManager,
 * INTERNAL_FORM_MANAGER,
 *}
 * @returns {() => void}
 */
function createAdd(_a) {
    var setRepetitions = _a.setRepetitions, repetitions = _a.repetitions, initialData = _a.initialData, repetitionFormData = _a.repetitionFormData, setRepetitionFormData = _a.setRepetitionFormData, cloneFormManager = _a.cloneFormManager, INTERNAL_FORM_MANAGER = _a.INTERNAL_FORM_MANAGER;
    return function () {
        setRepetitions(__spreadArray(__spreadArray([], repetitions, true), [__assign({}, initialData)], false));
        setRepetitionFormData(__spreadArray(__spreadArray([], repetitionFormData, true), [
            cloneFormManager(INTERNAL_FORM_MANAGER, initialData),
        ], false));
    };
}
/**
 * A factory function used to generate a `render` function, which
 * is used by the Repeater component to render a single repetition.
 * @date 10/30/2022 - 12:59:14 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {CreateFactoryArgs<Repetition, F>} {
 * renderer,
 * }
 * @returns {(rep: Repetition, formManager?: any) => any}
 */
function createRender(_a) {
    var renderer = _a.renderer, form = _a.form;
    return function (rep, formManager) {
        return Object.entries((formManager === null || formManager === void 0 ? void 0 : formManager._initialData) ? formManager === null || formManager === void 0 ? void 0 : formManager._initialData : {})
            .sort(byOriginalOrder(form))
            .map(function (_a, i) {
            var key = _a[0], value = _a[1];
            return (react_1.default.createElement(react_1.default.Fragment, { key: "key-" + key + "-" + value + "-" + i }, renderer({ name: key, value: value, repetition: rep, formManager: formManager, form: form })));
        });
    };
}
/**
 * A factory for creating a "remove" function, which is
 * used to remove a repetition from the Repeater.
 *
 * @date 10/30/2022 - 12:57:47 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {CreateFactoryArgs<Repetition>} {
 *  setRepetitions,
 *  setRepetitionFormData,
 *  repetitionFormData,
 *  repetitions,
 * }
 * @returns {(index?: number) => void}
 */
function createRemove(_a) {
    var setRepetitions = _a.setRepetitions, setRepetitionFormData = _a.setRepetitionFormData, repetitionFormData = _a.repetitionFormData, repetitions = _a.repetitions;
    return function (index) {
        if (index === undefined) {
            setRepetitions(repetitions.filter(function (_, i) { return i !== repetitions.length - 1; }));
            setRepetitionFormData(repetitionFormData.filter(function (_, i) { return i !== repetitions.length - 1; }));
        }
        else {
            setRepetitions(repetitions.filter(function (_, i) { return i !== index; }));
            setRepetitionFormData(repetitionFormData.filter(function (_, i) { return i !== index; }));
        }
    };
}
/**
 * A hook that estanlishes an internal counter, and returns a function that returns the next increment of that counter.
 * @date 10/30/2022 - 12:56:42 PM
 *
 * @returns {() => number}
 */
var useCounter = function () {
    var counter = (0, react_2.useState)(function () {
        var count = 0;
        return function () { return count++; };
    })[0];
    return counter;
};
function byOriginalOrder(form) {
    if (!form)
        return function () { return 0; };
    return function (_a, _b) {
        var nameA = _a[0];
        var nameB = _b[0];
        return (form.content.findIndex(function (q) { return nameA.startsWith(q.label); }) -
            form.content.findIndex(function (q) { return nameB.startsWith(q.label); }));
    };
}
/**
 * A factory for creating a "move" function, which is
 * used to move a repetition up or down in the Repeater.
 *
 * @date 10/30/2022 - 12:57:47 PM
 *
 * @template Repetition extends FormDataStructure<keyof Repetition>
 * @param {CreateFactoryArgs<Repetition>} {
 *  setRepetitions,
 *  setRepetitionFormData,
 *  repetitionFormData,
 *  repetitions,
 * }
 * @returns {(index: number, direction: 'UP' | 'DOWN') => void}
 */
function createMove(_a) {
    var setRepetitions = _a.setRepetitions, setRepetitionFormData = _a.setRepetitionFormData, repetitionFormData = _a.repetitionFormData, repetitions = _a.repetitions;
    return function (index, direction) {
        var _a, _b, _c, _d;
        if (direction === "UP" && index > 0) {
            _a = [
                repetitions[index - 1],
                repetitions[index],
            ], repetitions[index] = _a[0], repetitions[index - 1] = _a[1];
            _b = [
                repetitionFormData[index - 1],
                repetitionFormData[index],
            ], repetitionFormData[index] = _b[0], repetitionFormData[index - 1] = _b[1];
        }
        else if (direction === "DOWN" && index < repetitions.length - 1) {
            _c = [
                repetitions[index + 1],
                repetitions[index],
            ], repetitions[index] = _c[0], repetitions[index + 1] = _c[1];
            _d = [
                repetitionFormData[index + 1],
                repetitionFormData[index],
            ], repetitionFormData[index] = _d[0], repetitionFormData[index + 1] = _d[1];
        }
        setRepetitions(__spreadArray([], repetitions, true));
        setRepetitionFormData(__spreadArray([], repetitionFormData, true));
    };
}
